import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';

type Props = {
  open: boolean;
  title?: string;
  onClose: () => void;
  onScan: (value: string) => void;
};

const BarcodeScannerDialog: React.FC<Props> = ({ open, title = 'סריקת ברקוד', onClose, onScan }) => {
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const elementId = useMemo(
    () =>
      `barcode-scanner-${typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2)}`,
    []
  );

  const findBackCamera = useCallback(async (): Promise<string | null> => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (!devices.length) return null;

      let backCamera = devices.find((device) => {
        const label = (device.label || '').toLowerCase();
        const id = (device.id || '').toLowerCase();
        return (
          label.includes('back') ||
          label.includes('rear') ||
          label.includes('environment') ||
          label.includes('אחורית') ||
          id.includes('environment') ||
          id.includes('back') ||
          id.includes('rear')
        );
      });

      if (!backCamera) backCamera = devices[devices.length - 1];
      return (backCamera || devices[0]).id;
    } catch (error) {
      console.error('Error getting cameras:', error);
      return null;
    }
  }, []);

  const stopAndClear = useCallback(async () => {
    setScanning(false);
    if (!scannerRef.current) return;
    try {
      if (scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
      scannerRef.current.clear();
    } catch (e) {
      console.warn('Error clearing scanner:', e);
      try {
        scannerRef.current.clear();
      } catch {
        // ignore
      }
    } finally {
      scannerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(async () => {
    await stopAndClear();
    onClose();
  }, [onClose, stopAndClear]);

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      if (!open || scannerRef.current) return;
      await new Promise((r) => setTimeout(r, 250));
      if (!mounted) return;

      try {
        setScanning(true);
        const cameraId = await findBackCamera();
        if (!cameraId) {
          setScanning(false);
          return;
        }

        const html5QrCode = new Html5Qrcode(elementId);
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          cameraId,
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
          (decodedText) => {
            onScan(decodedText);
            handleClose();
          },
          () => {
            // ignore scan errors / no code found
          }
        );
      } catch (error) {
        console.error('Error initializing barcode scanner:', error);
        setScanning(false);
        if (scannerRef.current) {
          scannerRef.current = null;
        }
      }
    };

    if (open) start();

    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .then(() => {
            scannerRef.current?.clear();
            scannerRef.current = null;
          })
          .catch(() => {
            try {
              scannerRef.current?.clear();
            } catch {
              // ignore
            }
            scannerRef.current = null;
          });
      }
      setScanning(false);
    };
  }, [elementId, findBackCamera, handleClose, onScan, open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          direction: 'rtl',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
          {scanning ? (
            <>
              <Box id={elementId} sx={{ width: '100%', minHeight: 300 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                הצב את הברקוד מול המצלמה
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              מפעיל מצלמה...
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          ביטול
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScannerDialog;

